// src/pages/ActivitiesPage.js
import { useState, useEffect } from 'react';

import { useAuth } from '../context/AuthContext';
import { getActivities, postActivity } from '../api';
import NavBar from '../components/shared/NavBar';

const NAVY  = '#1B3A6B';
const GOLD  = '#C9A84C';
const RED   = '#c0392b';
const GREEN = '#1a6b3a';

const ACTIVITY_TYPES = [
  { value: 'reunion',  label: '🎊 Reunion',   bg: '#d6eaf8', color: NAVY },
  { value: 'social',   label: '🥂 Social',    bg: '#d4edda', color: GREEN },
  { value: 'charity',  label: '❤️ Charity',   bg: '#fdecea', color: RED },
  { value: 'sports',   label: '⚽ Sports',    bg: '#fff3cd', color: '#856404' },
  { value: 'memorial', label: '🕯️ Memorial',  bg: '#f0f0f0', color: '#555' },
  { value: 'general',  label: '📌 General',   bg: '#f0f4f8', color: '#444' },
];

export default function ActivitiesPage() {
  const { role }        = useAuth();
  const isExecutive     = role === 'executive';

  const [activities, setActivities] = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [showForm,   setShowForm]   = useState(false);
  const [posting,    setPosting]    = useState(false);
  const [expanded,   setExpanded]   = useState(null);
  const [typeFilter, setTypeFilter] = useState('');

  const [form, setForm] = useState({
    title:         '',
    description:   '',
    date:          new Date().toISOString().split('T')[0],
    drivePhotoLink:'',
    type:          'general'
  });

  useEffect(() => {
    loadActivities();
  }, []);

  useEffect(() => {
    let list = [...activities];
    if (typeFilter) {
      list = list.filter(a => a.type === typeFilter);
    }
    setFiltered(list);
  }, [typeFilter, activities]);

  async function loadActivities() {
    setLoading(true);
    try {
      const result = await getActivities();
      setActivities(result.activities || []);
      setFiltered(result.activities || []);
    } catch (err) {
      setError('Could not load activities: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  const handlePost = async () => {
    setError('');
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    setPosting(true);
    try {
      await postActivity(form);
      setSuccess('Activity posted successfully!');
      setForm({
        title: '', description: '',
        date: new Date().toISOString().split('T')[0],
        drivePhotoLink: '', type: 'general'
      });
      setShowForm(false);
      await loadActivities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to post: ' + err.message);
    } finally {
      setPosting(false);
    }
  };


  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getTypeStyle = (type) =>
    ACTIVITY_TYPES.find(t => t.value === type) || ACTIVITY_TYPES[5];

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
        <NavBar />

      <div style={{
        maxWidth: '900px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '12px'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
              🖼️ Activities & Gallery
            </h2>
            <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
              {loading ? 'Loading...'
                : `${activities.length} activit${activities.length !== 1 ? 'ies' : 'y'} posted`}
            </p>
          </div>
          {isExecutive && (
            <button
              onClick={() => {
                setShowForm(!showForm);
                setError('');
              }}
              style={{
                background: GOLD, color: NAVY,
                border: 'none', borderRadius: '8px',
                padding: '10px 20px', cursor: 'pointer',
                fontWeight: 'bold', fontSize: '14px'
              }}
            >
              {showForm ? '✕ Cancel' : '+ Post Activity'}
            </button>
          )}
        </div>

        {/* Type Filter */}
        <div style={{
          display: 'flex', gap: '8px',
          marginBottom: '20px', flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setTypeFilter('')}
            style={{
              padding: '7px 14px',
              background: !typeFilter ? NAVY : '#fff',
              color: !typeFilter ? '#fff' : '#555',
              border: `2px solid ${!typeFilter ? NAVY : '#e0e0e0'}`,
              borderRadius: '20px', cursor: 'pointer',
              fontSize: '13px', fontWeight: '600'
            }}
          >
            All ({activities.length})
          </button>
          {ACTIVITY_TYPES.map(t => {
            const count  = activities.filter(
              a => a.type === t.value
            ).length;
            const active = typeFilter === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setTypeFilter(
                  typeFilter === t.value ? '' : t.value
                )}
                style={{
                  padding: '7px 14px',
                  background: active ? t.color : t.bg,
                  color: active ? '#fff' : t.color,
                  border: `2px solid ${t.color}`,
                  borderRadius: '20px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600'
                }}
              >
                {t.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Success / Error */}
        {success && (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: GREEN,
            fontSize: '14px', fontWeight: '600'
          }}>
            ✓ {success}
          </div>
        )}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: RED, fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Post Form */}
        {showForm && isExecutive && (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px', marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: `2px solid ${GOLD}`
          }}>
            <h3 style={{
              color: NAVY, margin: '0 0 20px', fontSize: '16px'
            }}>
              Post New Activity
            </h3>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: '16px'
            }}>

              {/* Activity Type */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '8px'
                }}>
                  Activity Type
                </label>
                <div style={{
                  display: 'flex', gap: '8px', flexWrap: 'wrap'
                }}>
                  {ACTIVITY_TYPES.map(t => {
                    const active = form.type === t.value;
                    return (
                      <button
                        key={t.value}
                        onClick={() => setForm(p => ({
                          ...p, type: t.value
                        }))}
                        style={{
                          padding: '7px 14px',
                          background: active ? t.color : t.bg,
                          color: active ? '#fff' : t.color,
                          border: `2px solid ${t.color}`,
                          borderRadius: '20px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: '600'
                        }}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '6px'
                }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(p => ({
                    ...p, title: e.target.value
                  }))}
                  placeholder="e.g. 30th Anniversary Reunion Dinner"
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Date */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '6px'
                }}>
                  Activity Date
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(p => ({
                    ...p, date: e.target.value
                  }))}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '6px'
                }}>
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(p => ({
                    ...p, description: e.target.value
                  }))}
                  placeholder="Describe the activity, who attended, highlights..."
                  rows={4}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', outline: 'none',
                    boxSizing: 'border-box', resize: 'vertical',
                    fontFamily: 'Arial, sans-serif'
                  }}
                />
              </div>

              {/* Google Drive Photo Link */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '6px'
                }}>
                  Google Drive Photo Link (optional)
                </label>
                <input
                  type="url"
                  value={form.drivePhotoLink}
                  onChange={e => setForm(p => ({
                    ...p, drivePhotoLink: e.target.value
                  }))}
                  placeholder="https://drive.google.com/drive/folders/..."
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{
                  fontSize: '12px', color: '#888', margin: '6px 0 0'
                }}>
                  Upload photos to Google Drive first, then paste the
                  shared folder link here. Make sure the folder is
                  shared with "Anyone with the link can view".
                </p>
              </div>

              {/* Submit */}
              <button
                onClick={handlePost}
                disabled={posting}
                style={{
                  padding: '12px 24px',
                  background: posting ? '#ccc' : NAVY,
                  color: '#fff', border: 'none',
                  borderRadius: '8px',
                  cursor: posting ? 'not-allowed' : 'pointer',
                  fontSize: '15px', fontWeight: 'bold',
                  alignSelf: 'flex-end'
                }}
              >
                {posting ? 'Posting...' : 'Post Activity'}
              </button>
            </div>
          </div>
        )}

        {/* Activities List */}
        {loading ? (
          <div style={{
            textAlign: 'center', padding: '60px',
            color: '#666', fontSize: '14px'
          }}>
            Loading activities...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '60px', textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              🖼️
            </div>
            <h3 style={{ color: NAVY, margin: '0 0 8px' }}>
              {typeFilter
                ? `No ${typeFilter} activities yet`
                : 'No activities posted yet'}
            </h3>
            <p style={{ color: '#666', margin: 0 }}>
              {isExecutive
                ? 'Post your first activity using the button above.'
                : 'Check back for photos and activity updates.'}
            </p>
          </div>
        ) : (
          <div className="activities-grid" style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fill, minmax(380px, 1fr))',
            gap: '20px'
          }}>
            {filtered.map(activity => {
              const typeStyle = getTypeStyle(activity.type);
              const isExp     = expanded === activity.id;
              const hasPhotos = !!activity.drivePhotoLink;

              return (
                <div
                  key={activity.id}
                  style={{
                    background: '#fff', borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                    border: '2px solid transparent',
                    transition: 'border-color 0.2s'
                  }}
                  onMouseEnter={e =>
                    e.currentTarget.style.borderColor = GOLD
                  }
                  onMouseLeave={e =>
                    e.currentTarget.style.borderColor = 'transparent'
                  }
                >
                  {/* Card colour bar */}
                  <div style={{
                    height: '6px',
                    background: typeStyle.color
                  }} />

                  {/* Card Content */}
                  <div style={{ padding: '20px' }}>

                    {/* Type badge + date */}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: '12px',
                      flexWrap: 'wrap', gap: '8px'
                    }}>
                      <span style={{
                        background: typeStyle.bg,
                        color: typeStyle.color,
                        padding: '3px 10px', borderRadius: '10px',
                        fontSize: '12px', fontWeight: 'bold'
                      }}>
                        {typeStyle.label}
                      </span>
                      <span style={{
                        fontSize: '12px', color: '#888'
                      }}>
                        📅 {formatDate(activity.date)}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 style={{
                      margin: '0 0 10px', color: NAVY,
                      fontSize: '16px', fontWeight: 'bold'
                    }}>
                      {activity.title}
                    </h3>

                    {/* Description */}
                    {activity.description && (
                      <div>
                        <p style={{
                          margin: '0 0 12px', fontSize: '14px',
                          color: '#555', lineHeight: '1.6',
                          display: isExp ? 'block' : '-webkit-box',
                          WebkitLineClamp: isExp ? 'none' : 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: isExp ? 'visible' : 'hidden'
                        }}>
                          {activity.description}
                        </p>
                        {activity.description.length > 150 && (
                          <button
                            onClick={() =>
                              setExpanded(isExp ? null : activity.id)
                            }
                            style={{
                              background: 'none', border: 'none',
                              color: NAVY, cursor: 'pointer',
                              fontSize: '13px', fontWeight: '600',
                              padding: 0, marginBottom: '12px'
                            }}
                          >
                            {isExp ? '▲ Show less' : '▼ Read more'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div style={{
                      paddingTop: '12px',
                      borderTop: '1px solid #f0f0f0',
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', flexWrap: 'wrap', gap: '8px'
                    }}>
                      <span style={{
                        fontSize: '12px', color: '#aaa'
                      }}>
                        Posted by {activity.postedBy}
                      </span>

                      {hasPhotos && (
                        
                         <a href={activity.drivePhotoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{
                            background: NAVY, color: '#fff',
                            padding: '6px 14px', borderRadius: '6px',
                            fontSize: '12px', fontWeight: 'bold',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center', gap: '4px'
                          }}
                        >
                          📸 View Photos
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}